import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreatePaymentLinkMutation } from "@/store/charges/apis";
import { formatPhoneNumber } from "@/lib/utils";
import { Move } from "@/types";
import { useGetMoveQuery } from "@/store/moves/apis";
import { useEffect, useMemo, useRef, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { SheetClose } from "@/components/ui/sheet";
import { useSelector } from "react-redux";
import { getSelectedMover } from "@/store/mover/slice";

type FormData = {
  move_id: number | undefined;
  amount: string;
  purpose: string;
  memo: string;
  emails: string[];
  phone: string | null;
  sendToEmail: boolean;
  sendToPhone: boolean;
  addProcessingFee: boolean;
  processingFee: string;
};

const formSchema = z
  .object({
    move_id: z.number().min(1, "Move ID is required"),
    amount: z.string().min(1, "(required)"),
    processing_fee: z.number().optional(),
    purpose: z
      .string()
      .refine(
        (val) =>
          ["deposit", "final_payment", "additional_payment"].includes(val),
        {
          message: "(required)",
        }
      ),
    memo: z.string().optional(),
    emails: z.array(z.string().email()).optional(),
    phone: z.string().optional(),
    sendToEmail: z.boolean(),
    sendToPhone: z.boolean(),
    addProcessingFee: z.boolean(),
    processingFee: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.sendToEmail && !data.emails) {
        return false;
      }
      return true;
    },
    { message: "At least one email is required when email is enabled" }
  )
  .refine(
    (data) => {
      if (data.sendToPhone && !data.phone) {
        return false;
      }
      return true;
    },
    { message: "Phone number is required when SMS is enabled" }
  );

export function SendPaymentLink({
  initialMove,
  onSuccess,
}: {
  initialMove?: Move;
  onSuccess?: () => void;
}) {
  const { id } = useParams();
  const [createPaymentLink] = useCreatePaymentLinkMutation();
  const [move, setMove] = useState<Move | undefined>(initialMove);
  const { data: fetchedMove } = useGetMoveQuery(id, { skip: !!initialMove });
  const closeRef = useRef<HTMLButtonElement>(null);
  const mover = useSelector(getSelectedMover);

  const form = useForm<FormData>({
    defaultValues: {
      move_id: move?.id,
      amount: "",
      purpose: "",
      memo: "",
      emails: move?.customer_email ? [move.customer_email] : [],
      phone: move?.customer_mobile_phone
        ? formatPhoneNumber(move.customer_mobile_phone)
        : "",
      sendToEmail: true,
      sendToPhone: true,
      addProcessingFee: false,
      processingFee: String(mover?.mover_configuration?.cc_fee_percentage || 0),
    },
  });

  useEffect(() => {
    if (!initialMove) {
      setMove(fetchedMove);
    }
  }, [initialMove, fetchedMove]);

  useEffect(() => {
    if (move) {
      form.reset({
        move_id: move.id,
        amount: "",
        purpose: "",
        memo: "",
        emails: move?.customer_email ? [move.customer_email] : [],
        phone: move?.customer_mobile_phone
          ? formatPhoneNumber(move.customer_mobile_phone)
          : "",
        sendToEmail: true,
        sendToPhone: true,
        addProcessingFee: false,
        processingFee: String(
          mover?.mover_configuration?.cc_fee_percentage || 0
        ),
      });
    }
  }, [move, form]);

  const [submitting, setSubmitting] = useState(false);

  const validateAndSubmit = async (data: FormData) => {
    if (!move) {
      return;
    }

    setSubmitting(true);

    const validationResult = formSchema.safeParse(data);

    if (!validationResult.success) {
      const errors = validationResult.error.formErrors.fieldErrors;
      Object.entries(errors).forEach(([key, value]) => {
        form.setError(key as keyof FormData, {
          type: "manual",
          message: value?.[0],
        });
      });
      setSubmitting(false);
      return;
    }

    const processingFeeAmount = calculatedProcessingFee(
      parseFloat(data.amount),
      parseFloat(data.processingFee || "0"),
      data.addProcessingFee
    );

    try {
      await createPaymentLink({
        move_id: move.id,
        payment_link: {
          purpose: data.purpose,
          memo: data.memo,
          amount: parseFloat(data.amount) + processingFeeAmount,
          processing_fee_percentage: data.addProcessingFee
            ? parseFloat(data.processingFee || "0")
            : 0,
          processing_fee_amount: processingFeeAmount,
          emails: data.sendToEmail ? data.emails : [],
          phone: data.sendToPhone ? data.phone : null,
        },
        send: true,
      }).unwrap();

      if (onSuccess) {
        onSuccess();
      }
      closeRef.current?.click();
    } catch (error) {
      console.error("Failed to create payment link:", error);
      setSubmitting(false);
    }
  };

  const calculatedProcessingFee = (
    amount: number,
    percentage = 0,
    addProcessingFee = false
  ) => {
    if (!addProcessingFee) {
      return 0;
    }

    return (percentage / 100) * amount;
  };
  const totalAmount = useMemo(
    () =>
      parseFloat(form.watch("amount") || "0") +
      calculatedProcessingFee(
        parseFloat(form.watch("amount") || "0"),
        parseFloat(form.watch("processingFee") || "0"),
        form.watch("addProcessingFee")
      ),
    [form, form.watch("amount"), form.watch("processingFee")]
  );

  if (!move || !form.getValues("move_id")) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      {/* <Header move={move} /> */}
      <Form {...form}>
        <div>
          <h1 className="text-lg font-semibold mb-4">Send Link To Pay</h1>
          <form
            onSubmit={form.handleSubmit(validateAndSubmit)}
            className="space-y-4"
          >
            <div className="flex flex-row items-start gap-4">
              <FormField
                control={form.control}
                name="purpose"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="col-span-1">
                      Purpose
                      <span className="inline-block pl-1">
                        <FormMessage />
                      </span>
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Select purpose" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="deposit">Deposit</SelectItem>
                        <SelectItem value="final_payment">
                          Final Payment
                        </SelectItem>
                        <SelectItem value="additional_payment">
                          Additional Payment
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Amount
                      <span className="inline-block pl-1">
                        <FormMessage />
                      </span>
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-0 inset-y-0 flex items-center px-2 text-sm">
                          $
                        </span>
                        <Input
                          type="text"
                          onFocus={(e) => {
                            e.target.select();
                          }}
                          className="pl-6 text-sm w-24"
                          {...field}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col gap-1">
              <FormField
                control={form.control}
                name="addProcessingFee"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2">
                    <FormLabel>Add Credit Card Processing Fee</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(value) => {
                          if (!value) {
                            form.setValue("processingFee", "0");
                          }

                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="text-yellow-600 text-sm">
                Please check that customer is paying with a credit card.
                Surcharges on debit cards are unlawful in most states.
              </div>
            </div>

            {form.watch("addProcessingFee") && (
              <div className="space-y-2">
                <FormField
                  control={form.control}
                  name="processingFee"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Credit Card Surcharge</FormLabel>
                      <FormControl>
                        <div className="relative w-24">
                          <Input
                            type="text"
                            autoComplete="off"
                            className="pr-6 text-sm"
                            onFocus={(e) => {
                              e.target.select();
                            }}
                            {...field}
                          />
                          <span className="absolute right-2 inset-y-0 flex items-center text-sm">
                            %
                          </span>
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}

            <div>
              <div className="flex justify-between items-center text-sm">
                <span className="font-normal">Amount</span>
                <span className="font-normal">
                  {Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(parseFloat(form.watch("amount")) || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <div className="font-normal">Processing Fee</div>
                <div className="font-normal space-x-2">
                  <span>
                    {form.watch("addProcessingFee") &&
                      `(${form.watch("processingFee")}%)`}
                  </span>
                  <span>
                    {form.watch("addProcessingFee")
                      ? Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(
                          calculatedProcessingFee(
                            parseFloat(form.watch("amount") || "0"),
                            parseFloat(form.watch("processingFee") || "0"),
                            form.watch("addProcessingFee")
                          )
                        )
                      : "$0"}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center text-sm border-t border-gray-200 my-1 py-1">
                <span className="font-medium">Total Amount</span>
                <span className="font-medium">
                  {Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(totalAmount)}
                </span>
              </div>
            </div>

            <FormField
              control={form.control}
              name="memo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex flex-row justify-between">
                    Notes (customer can see this){" "}
                    <span className="text-xs text-gray-700">optional</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter additional information about the payment"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4 flex items-start">
              <div className="space-y-2 col-span-1">
                <div className="flex items-center space-x-2">
                  <Switch
                    className="data-[state=checked]:bg-black"
                    checked={form.watch("sendToEmail")}
                    onCheckedChange={(checked) => {
                      // Prevent unchecking if phone is already off
                      if (!checked && !form.getValues("sendToPhone")) {
                        return;
                      }
                      form.setValue("sendToEmail", checked);
                      if (!checked) {
                        form.setValue("emails", []);
                      } else {
                        form.setValue(
                          "emails",
                          move?.customer_email ? [move.customer_email] : []
                        );
                      }
                    }}
                  />
                  <FormLabel>Send to Email</FormLabel>
                </div>

                {form.watch("sendToEmail") && (
                  <FormField
                    control={form.control}
                    name="emails"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            placeholder="Enter emails separated by commas"
                            onChange={(e) =>
                              field.onChange(
                                e.target.value
                                  .split(",")
                                  .map((email) => email.trim())
                              )
                            }
                            value={field.value?.join(", ") || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Switch
                    className="data-[state=checked]:bg-black"
                    checked={form.watch("sendToPhone")}
                    onCheckedChange={(checked) => {
                      // Prevent unchecking if email is already off
                      if (!checked && !form.getValues("sendToEmail")) {
                        return;
                      }
                      form.setValue("sendToPhone", checked);
                      if (!checked) {
                        form.setValue("phone", "");
                      } else {
                        form.setValue(
                          "phone",
                          move?.customer_mobile_phone
                            ? formatPhoneNumber(move.customer_mobile_phone)
                            : ""
                        );
                      }
                    }}
                  />
                  <FormLabel>Send via Text</FormLabel>
                </div>

                {form.watch("sendToPhone") && (
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...field}
                            onChange={(e) => {
                              field.onChange(formatPhoneNumber(e.target.value));
                            }}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>

            <div className="flex gap-4">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Sending..." : "Send Payment Link"}
              </Button>
              <SheetClose ref={closeRef} asChild>
                <Button type="button" variant="secondary">
                  Cancel
                </Button>
              </SheetClose>
            </div>
          </form>
        </div>
        {/* <div className="hidden">
            <h1 className="text-xl font-semibold mb-6">Preview</h1>
            <div className="p-4 border-b md:border-r border-black">
              <div className="flex flex-row gap-4">
                <div className="w-1/2">
                  <h2 className="text-lg font-semibold">Amount</h2>
                  <p className="text-sm text-gray-500">
                    {form.getValues("amount")
                      ? Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(parseFloat(form.getValues("amount")))
                      : ""}
                  </p>
                </div>
              </div>
            </div>
          </div> */}
      </Form>
    </div>
  );
}
